/**
 * 文档分析页面
 * 支持用户自定义提示词选择和任务跟踪
 */
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { documentApi, configApi, jobsApi } from '../services/api';
import { CloudArrowUpIcon, DocumentIcon, PencilIcon, CheckIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { draftStorage } from '../utils/draftStorage';
import { usePrompts } from '../hooks/usePrompts';
import PromptSelector from '../components/PromptSelector';
import { PromptSelection } from '../types';

interface DocumentAnalysisProps {
  currentJobId: number | null;
  fileContent: string;
  sourceFileName: string | null;
  projectOverview: string;
  techRequirements: string;
  onFileUpload: (content: string) => void;
  onFileNameSet: (fileName: string | null) => void;
  onAnalysisComplete: (overview: string, requirements: string) => void;
  onJobCreated: (jobId: number) => void;
  onJobUpdated: () => void;
}

const DocumentAnalysis: React.FC<DocumentAnalysisProps> = ({
  currentJobId,
  fileContent,
  sourceFileName,
  projectOverview,
  techRequirements,
  onFileUpload,
  onFileNameSet,
  onAnalysisComplete,
  onJobCreated,
  onJobUpdated,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const promptInitializedRef = useRef(false);

  const [localOverview, setLocalOverview] = useState(projectOverview);
  const [localRequirements, setLocalRequirements] = useState(techRequirements);

  // 编辑状态
  const [editingField, setEditingField] = useState<'overview' | 'requirements' | null>(null);
  const [editValue, setEditValue] = useState('');

  // 高级设置展开状态
  const [showAdvanced, setShowAdvanced] = useState(false);

  // 默认提示词（从后端配置获取）
  const [defaultPrompts, setDefaultPrompts] = useState<{ overview: string; requirements: string }>({
    overview: '',
    requirements: ''
  });

  // 用户提示词管理
  const { prompts, createPrompt, updatePrompt, getDefaultPrompt, loading: promptsLoading } = usePrompts();

  // 选中的提示词
  const [selectedPrompts, setSelectedPrompts] = useState<PromptSelection>({
    overview_prompt_id: null,
    requirements_prompt_id: null,
    full_outline_prompt_id: null,
    chapter_content_prompt_id: null,
  });

  // 当前使用的提示词内容
  const [currentPromptContent, setCurrentPromptContent] = useState<{ overview: string; requirements: string }>({
    overview: '',
    requirements: '',
  });

  // 获取系统内置提示词
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const response = await configApi.getPrompts();
        if (response.data.success && response.data.prompts) {
          const docPrompts = response.data.prompts.document_analysis || {};
          setDefaultPrompts({
            overview: docPrompts.overview || '',
            requirements: docPrompts.requirements || ''
          });
        }
      } catch (error) {
        console.error('获取系统内置提示词失败:', error);
      }
    };
    fetchPrompts();
  }, []);

  // 初始化时自动选中用户的默认提示词
  useEffect(() => {
    if (promptsLoading || promptInitializedRef.current) return;

    promptInitializedRef.current = true;

    // 检查是否有用户设置的默认提示词
    const overviewDefault = getDefaultPrompt('overview');
    const requirementsDefault = getDefaultPrompt('requirements');

    setSelectedPrompts(prev => {
      const updated = { ...prev };
      if (overviewDefault) {
        updated.overview_prompt_id = overviewDefault.id;
        setCurrentPromptContent(c => ({ ...c, overview: overviewDefault.content }));
      }
      if (requirementsDefault) {
        updated.requirements_prompt_id = requirementsDefault.id;
        setCurrentPromptContent(c => ({ ...c, requirements: requirementsDefault.content }));
      }
      return updated;
    });
    // 如果没有默认提示词，保持 null（使用系统内置）
  }, [prompts, promptsLoading, getDefaultPrompt]);

  // 同步外部传入的值到本地状态
  useEffect(() => {
    setLocalOverview(projectOverview);
  }, [projectOverview]);

  useEffect(() => {
    setLocalRequirements(techRequirements);
  }, [techRequirements]);

  // 处理提示词选择
  const handlePromptSelect = (type: 'overview' | 'requirements', promptId: number | null, content: string) => {
    setSelectedPrompts(prev => ({
      ...prev,
      [`${type}_prompt_id`]: promptId,
    }));
    setCurrentPromptContent(prev => ({
      ...prev,
      [type]: content,
    }));
  };

  // 开始编辑
  const startEdit = (field: 'overview' | 'requirements') => {
    setEditingField(field);
    setEditValue(field === 'overview' ? localOverview : localRequirements);
  };

  // 保存编辑
  const saveEdit = () => {
    if (editingField === 'overview') {
      setLocalOverview(editValue);
      onAnalysisComplete(editValue, localRequirements);
    } else if (editingField === 'requirements') {
      setLocalRequirements(editValue);
      onAnalysisComplete(localOverview, editValue);
    }
    setEditingField(null);
    setEditValue('');
  };

  // 取消编辑
  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  // 下载原始文件
  const handleDownloadFile = async () => {
    if (currentJobId) {
      try {
        const response = await documentApi.downloadSourceFile(currentJobId);
        if (!response.ok) {
          throw new Error('下载失败');
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = sourceFileName || 'source_file';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('下载文件失败:', error);
        alert('下载文件失败，请重试');
      }
    } else if (fileContent) {
      // 没有关联任务时，下载提取的文本内容
      const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const baseName = sourceFileName?.replace(/\.[^/.]+$/, '') || 'source';
      a.download = `${baseName}_提取文本.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // 处理换行符的函数
  const normalizeLineBreaks = (text: string) => {
    if (!text) return text;
    return text
      .replace(/\\n/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');
  };

  // 流式显示状态
  const [currentAnalysisStep, setCurrentAnalysisStep] = useState<'overview' | 'requirements' | null>(null);
  const [streamingOverview, setStreamingOverview] = useState('');
  const [streamingRequirements, setStreamingRequirements] = useState('');

  // 公共的 ReactMarkdown 组件配置
  const markdownComponents = {
    p: ({ children }: any) => <p className="mb-3 leading-relaxed text-sm" style={{whiteSpace: 'pre-wrap', lineHeight: '1.5'}}>{children}</p>,
    ul: ({ children }: any) => <ul className="mb-4 pl-5 space-y-1.5 list-disc">{children}</ul>,
    ol: ({ children }: any) => <ol className="mb-4 pl-5 space-y-1.5 list-decimal">{children}</ol>,
    li: ({ children }: any) => <li className="text-sm leading-relaxed">{children}</li>,
    h1: ({ children }: any) => <h1 className="text-lg font-semibold mb-3 text-gray-900 border-b border-gray-200 pb-2">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-base font-semibold mb-2 text-gray-900">{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-sm font-semibold mb-2 text-gray-800">{children}</h3>,
    strong: ({ children }: any) => <strong className="font-semibold text-gray-900">{children}</strong>,
    em: ({ children }: any) => <em className="italic text-gray-700">{children}</em>,
    blockquote: ({ children }: any) => <blockquote className="border-l-4 border-green-200 pl-4 my-3 italic text-gray-600">{children}</blockquote>,
    code: ({ children }: any) => <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>,
    table: ({ children }: any) => <table className="w-full border-collapse border border-gray-300 my-3">{children}</table>,
    thead: ({ children }: any) => <thead className="bg-gray-50">{children}</thead>,
    th: ({ children }: any) => <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-xs">{children}</th>,
    td: ({ children }: any) => <td className="border border-gray-300 px-3 py-2 text-xs">{children}</td>,
    br: () => <br className="my-1" />,
    text: ({ children }: any) => <span style={{whiteSpace: 'pre-wrap'}}>{children}</span>,
  };

  // 流式显示的紧凑样式配置
  const streamingComponents = {
    p: ({ children }: any) => <p className="mb-2 leading-tight text-xs text-blue-400" style={{whiteSpace: 'pre-wrap', lineHeight: '1.3'}}>{children}</p>,
    ul: ({ children }: any) => <ul className="mb-2 pl-3 space-y-0.5 list-disc text-blue-400">{children}</ul>,
    ol: ({ children }: any) => <ol className="mb-2 pl-3 space-y-0.5 list-decimal text-blue-400">{children}</ol>,
    li: ({ children }: any) => <li className="text-xs leading-tight text-blue-400">{children}</li>,
    h1: ({ children }: any) => <h1 className="text-sm font-semibold mb-2 text-blue-500 border-b border-blue-200 pb-1">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-xs font-semibold mb-1.5 text-blue-500">{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-xs font-semibold mb-1 text-blue-400">{children}</h3>,
    strong: ({ children }: any) => <strong className="font-semibold text-blue-500">{children}</strong>,
    em: ({ children }: any) => <em className="italic text-blue-400">{children}</em>,
    blockquote: ({ children }: any) => <blockquote className="border-l-2 border-blue-300 pl-2 my-1.5 italic text-blue-400">{children}</blockquote>,
    code: ({ children }: any) => <code className="bg-blue-50 px-1 py-0.5 rounded text-xs font-mono text-blue-400">{children}</code>,
    table: ({ children }: any) => <table className="w-full border-collapse border border-blue-200 my-2">{children}</table>,
    thead: ({ children }: any) => <thead className="bg-blue-50">{children}</thead>,
    th: ({ children }: any) => <th className="border border-blue-200 px-2 py-1 text-left font-semibold text-xs text-blue-500">{children}</th>,
    td: ({ children }: any) => <td className="border border-blue-200 px-2 py-1 text-xs text-blue-400">{children}</td>,
    br: () => <br className="my-0.5" />,
    text: ({ children }: any) => <span className="text-blue-400" style={{whiteSpace: 'pre-wrap'}}>{children}</span>,
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setUploading(true);
      setMessage(null);

      // 创建新任务
      const jobName = file.name.replace(/\.[^/.]+$/, '');
      const jobResponse = await jobsApi.create(jobName);
      const newJobId = jobResponse.data.id;
      onJobCreated(newJobId);

      // 上传文件（关联任务）
      const response = await documentApi.uploadFile(file, newJobId);

      if (response.data.success && response.data.file_content) {
        // 清空之前的草稿
        draftStorage.clearAll();
        onFileUpload(response.data.file_content);
        onFileNameSet(file.name);
        setMessage({ type: 'success', text: response.data.message });
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || '文件上传失败' });
    } finally {
      setUploading(false);
    }
  };

  const handleAnalysis = async () => {
    if (!fileContent) {
      setMessage({ type: 'error', text: '请先上传文档' });
      return;
    }

    try {
      setAnalyzing(true);
      setMessage(null);
      setStreamingOverview('');
      setStreamingRequirements('');

      let overviewResult = '';
      let requirementsResult = '';

      const decoder = new TextDecoder();

      // 处理流式响应的通用函数
      const processStream = async (response: Response, onChunk: (chunk: string) => void) => {
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('无法读取响应流');
        }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                break;
              }
              try {
                const parsed = JSON.parse(data);
                if (parsed.chunk) {
                  onChunk(parsed.chunk);
                }
              } catch (e) {
                // 忽略JSON解析错误
              }
            }
          }
        }
      };

      // 获取实际使用的提示词
      const overviewPrompt = currentPromptContent.overview || defaultPrompts.overview;
      const requirementsPrompt = currentPromptContent.requirements || defaultPrompts.requirements;

      // 第一步：分析项目概述
      setCurrentAnalysisStep('overview');
      const overviewResponse = await documentApi.analyzeDocumentStream({
        file_content: fileContent,
        analysis_type: 'overview',
        custom_prompt: currentPromptContent.overview ? overviewPrompt : undefined,
      });

      await processStream(overviewResponse, (chunk) => {
        overviewResult += chunk;
        const normalizedContent = normalizeLineBreaks(overviewResult);
        setStreamingOverview(normalizedContent);
      });

      const finalOverview = normalizeLineBreaks(overviewResult);
      setLocalOverview(finalOverview);

      // 第二步：分析技术评分要求
      setCurrentAnalysisStep('requirements');
      const requirementsResponse = await documentApi.analyzeDocumentStream({
        file_content: fileContent,
        analysis_type: 'requirements',
        custom_prompt: currentPromptContent.requirements ? requirementsPrompt : undefined,
      });

      await processStream(requirementsResponse, (chunk) => {
        requirementsResult += chunk;
        const normalizedContent = normalizeLineBreaks(requirementsResult);
        setStreamingRequirements(normalizedContent);
      });

      const finalRequirements = normalizeLineBreaks(requirementsResult);
      setLocalRequirements(finalRequirements);

      // 完成后更新父组件状态
      onAnalysisComplete(overviewResult, requirementsResult);

      // 更新任务
      if (currentJobId) {
        await jobsApi.update(currentJobId, {
          project_overview: overviewResult,
          tech_requirements: requirementsResult,
        });
        onJobUpdated();
      }

      setMessage({ type: 'success', text: '标书解析完成' });

      // 清空流式内容
      setStreamingOverview('');
      setStreamingRequirements('');
      setCurrentAnalysisStep(null);

    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '标书解析失败' });
      setStreamingOverview('');
      setStreamingRequirements('');
      setCurrentAnalysisStep(null);
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* 文件上传区域 */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">📄 文档上传（招标文件）</h2>

        {/* 已上传文件显示 */}
        {fileContent && (uploadedFile || sourceFileName) ? (
          <div className="border border-green-200 rounded-lg p-4 bg-green-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <DocumentIcon className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {uploadedFile ? uploadedFile.name : sourceFileName}
                  </p>
                  <p className="text-xs text-gray-500">
                    已上传，可重新上传替换
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDownloadFile}
                  className="inline-flex items-center px-3 py-1.5 text-sm text-primary-600 hover:text-primary-800"
                  title="下载原始文件"
                >
                  <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                  下载
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  重新上传
                </button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.doc"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
            <div className="mt-2">
              <p className="text-sm text-gray-600">
                点击选择文件或拖拽文件到这里
              </p>
              <p className="text-xs text-gray-500 mt-1">
                支持 PDF 和 Word 文档，最大 10MB
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.doc"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {uploading && (
          <div className="mt-4 text-center">
            <div className="inline-flex items-center px-4 py-2 text-sm text-blue-600">
              <div className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              正在上传和处理文件...
            </div>
          </div>
        )}
      </div>

      {/* 文档分析区域 */}
      {fileContent && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🔍 文档分析</h2>

          <div className="flex justify-center mb-6">
            <button
              onClick={handleAnalysis}
              disabled={analyzing}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {analyzing ? (
                <>
                  <div className="animate-spin -ml-1 mr-3 h-5 w-5 text-white">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  {currentAnalysisStep === 'overview' ? '正在分析项目概述...' :
                   currentAnalysisStep === 'requirements' ? '正在分析技术评分要求...' :
                   '正在解析标书...'}
                </>
              ) : (
                <>
                  <DocumentIcon className="w-5 h-5 mr-2" />
                  解析标书
                </>
              )}
            </button>

            {/* 高级设置展开按钮 */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="ml-3 inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="高级设置"
            >
              {showAdvanced ? (
                <ChevronUpIcon className="w-5 h-5" />
              ) : (
                <ChevronDownIcon className="w-5 h-5" />
              )}
              <span className="ml-2">提示词设置</span>
            </button>
          </div>

          {/* 提示词设置区域 */}
          {showAdvanced && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="text-sm font-medium text-gray-800 mb-4">提示词设置</h4>
              <p className="text-xs text-gray-500 mb-4">
                选择已保存的提示词或使用默认提示词。可以保存新的自定义提示词供后续使用。
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 项目概述提示词选择器 */}
                <PromptSelector
                  promptType="overview"
                  label="项目概述提示词"
                  selectedPromptId={selectedPrompts.overview_prompt_id}
                  defaultPrompt={defaultPrompts.overview}
                  prompts={prompts}
                  onPromptSelect={(id, content) => handlePromptSelect('overview', id, content)}
                  onCreatePrompt={async (name, content, isDefault) => {
                    return await createPrompt(name, 'overview', content, isDefault);
                  }}
                  onUpdatePrompt={updatePrompt}
                  loading={promptsLoading}
                />

                {/* 技术评分要求提示词选择器 */}
                <PromptSelector
                  promptType="requirements"
                  label="技术评分要求提示词"
                  selectedPromptId={selectedPrompts.requirements_prompt_id}
                  defaultPrompt={defaultPrompts.requirements}
                  prompts={prompts}
                  onPromptSelect={(id, content) => handlePromptSelect('requirements', id, content)}
                  onCreatePrompt={async (name, content, isDefault) => {
                    return await createPrompt(name, 'requirements', content, isDefault);
                  }}
                  onUpdatePrompt={updatePrompt}
                  loading={promptsLoading}
                />
              </div>
            </div>
          )}

          {/* 流式分析内容显示 */}
          {analyzing && (((currentAnalysisStep === 'overview') && streamingOverview) || ((currentAnalysisStep === 'requirements') && streamingRequirements)) && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-medium text-blue-800 mb-3">
                {currentAnalysisStep === 'overview' ? '正在分析项目概述...' : '正在分析技术评分要求...'}
              </h4>
              <div className="bg-white p-3 rounded-lg border border-gray-200 max-h-64 overflow-y-auto shadow-sm">
                <div className="text-xs prose prose-sm max-w-none">
                  <ReactMarkdown components={streamingComponents}>
                    {currentAnalysisStep === 'overview' ? streamingOverview : streamingRequirements}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          )}


          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 项目概述 */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  项目概述
                </label>
                {editingField !== 'overview' && localOverview && !analyzing && (
                  <button
                    onClick={() => startEdit('overview')}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="编辑"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="w-full p-4 border border-gray-300 rounded-lg focus-within:ring-blue-500 focus-within:border-blue-500 max-h-80 overflow-y-auto bg-white shadow-sm">
                {editingField === 'overview' ? (
                  <div className="flex flex-col h-full">
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="flex-1 w-full min-h-[200px] border-0 focus:ring-0 focus:outline-none resize-none text-sm text-gray-800"
                      placeholder="项目概述内容..."
                    />
                    <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={cancelEdit}
                        className="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                      >
                        <XMarkIcon className="w-4 h-4 mr-1" />
                        取消
                      </button>
                      <button
                        onClick={saveEdit}
                        className="inline-flex items-center px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
                      >
                        <CheckIcon className="w-4 h-4 mr-1" />
                        保存
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="prose prose-sm max-w-none text-gray-800">
                    <ReactMarkdown components={markdownComponents}>
                      {localOverview || '项目概述将在这里显示...'}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>

            {/* 技术评分要求 */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  技术评分要求
                </label>
                {editingField !== 'requirements' && localRequirements && !analyzing && (
                  <button
                    onClick={() => startEdit('requirements')}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="编辑"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="w-full p-4 border border-gray-300 rounded-lg focus-within:ring-green-500 focus-within:border-green-500 max-h-80 overflow-y-auto bg-white shadow-sm">
                {editingField === 'requirements' ? (
                  <div className="flex flex-col h-full">
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="flex-1 w-full min-h-[200px] border-0 focus:ring-0 focus:outline-none resize-none text-sm text-gray-800"
                      placeholder="技术评分要求内容..."
                    />
                    <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={cancelEdit}
                        className="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                      >
                        <XMarkIcon className="w-4 h-4 mr-1" />
                        取消
                      </button>
                      <button
                        onClick={saveEdit}
                        className="inline-flex items-center px-3 py-1 text-sm text-white bg-green-600 rounded hover:bg-green-700"
                      >
                        <CheckIcon className="w-4 h-4 mr-1" />
                        保存
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="prose prose-sm max-w-none text-gray-800">
                    <ReactMarkdown components={markdownComponents}>
                      {localRequirements || '技术评分要求将在这里显示...'}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 消息提示 */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.type === 'success'
            ? 'bg-green-100 text-green-700 border border-green-200'
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default DocumentAnalysis;